import React from "react";
//#region types
import { SimpleDatePickerProps } from "models/propTypes/datepickerPropTypes";
import { IconDefinitionConstants } from "utils/constants";
//#endregion
//#region components
import DatePickerLocalization from "./datePickerLocalization";
import Icon from "../icon";
import { InputAdornment, TextField, ClickAwayListener } from "@mui/material";
import { DesktopDatePicker } from "@mui/x-date-pickers/DesktopDatePicker";
//#endregion
import cssExports from "theme/styles/main.module.scss";
import clsx from "clsx";
import { CSSHelper } from "utils/helpers";

const SimpleDatePicker: React.FC<SimpleDatePickerProps> = (props) => {
  const { success, displayType, overridePlaceholder, ...datepickerProps } = props;
  const [shrinkLabel, setShrinkLabel] = React.useState<boolean>(!!props.value);

  const [pickerOpened, setPickerOpened] = React.useState<boolean>(false);

  const inputRef = React.useRef<HTMLInputElement | null>(null);

  React.useEffect(() => {
    if (!props.value && shrinkLabel) setShrinkLabel(false);
    else if (props.value && !shrinkLabel) setShrinkLabel(true);
  }, [props.value]);

  const onFocus = (event: React.FocusEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    props.InputProps && props.InputProps.onFocus && props.InputProps.onFocus(event);
    props.onFocus && props.onFocus(event);
    setShrinkLabel(true);
    setPickerOpened(true);
  };

  const onBlur = (event: React.FocusEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    props.InputProps && props.InputProps.onBlur && props.InputProps.onBlur(event);
    props.onBlur && props.onBlur(event);
    !props.value && setShrinkLabel(false);
  };

  const onAccept = () => {
    setShrinkLabel(true);
  };

  const onPickerOpened = (event?: React.MouseEvent<HTMLElement>) => {
    event?.stopPropagation();
    setPickerOpened(true);
  };

  const onPickerClosed = () => {
    if (pickerOpened) setPickerOpened(false);
  };

  const onChange = (date: Date | null, value?: string | null | undefined) => {
    props.onChange(date ? new Date(date.setHours(0, 0, 0, 0)) : undefined, value ?? undefined);
    setTimeout(() => {
      props.onBlur && props.onBlur({ target: { name: props.name } } as React.FocusEvent<HTMLInputElement>);
    });
  };

  const onClick = (event: React.MouseEvent<HTMLElement>) => {
    event.stopPropagation();
    inputRef?.current?.focus();
  };

  return (
    <ClickAwayListener mouseEvent="onMouseDown" touchEvent="onTouchStart" onClickAway={onPickerClosed}>
      <span
        role="presentation"
        className={clsx(
          cssExports.iconInputContainer,
          cssExports.datePickerInput,
          pickerOpened ? cssExports.pickerOpened : ""
        )}
      >
        <DatePickerLocalization>
          <DesktopDatePicker
            {...datepickerProps}
            value={props.value ?? null}
            onChange={onChange}
            defaultCalendarMonth={props.defaultCalendarMonth}
            onAccept={onAccept}
            className={props.className}
            reduceAnimations
            components={{
              OpenPickerIcon: () => (
                <span onClick={onPickerOpened}>
                  {props.keyboardIcon ?? (
                    <Icon icon={IconDefinitionConstants.WCalendar} className={cssExports.inputIcon} />
                  )}
                </span>
              ),
            }}
            inputRef={inputRef}
            InputAdornmentProps={{
              position: "start",
              className: cssExports.inputAdornedStart,
            }}
            InputProps={{
              classes: {
                root: clsx(
                  cssExports.inputRoot,
                  cssExports.iconInput,
                  shrinkLabel ? cssExports.shrinkedLabelInput : ""
                ),
                input: cssExports.inputInput,
                focused: cssExports.focusedInput,
                error: cssExports.errorInput,
                disabled: cssExports.disabledInput,
              },
              onBlur: onBlur,
              onFocus: onFocus,
              autoComplete: "off",
              endAdornment: (
                <>
                  {(success as boolean) ? (
                    <InputAdornment position="end" className={clsx(cssExports.inputAdornedEnd, cssExports.success)}>
                      <Icon icon={IconDefinitionConstants.WCheck} className={cssExports.inputIcon} />
                    </InputAdornment>
                  ) : (
                    <></>
                  )}
                  {props.disabled ? (
                    <InputAdornment position="end" className={cssExports.inputAdornedEnd}>
                      <Icon icon={IconDefinitionConstants.WLock} className={cssExports.inputIcon} />
                    </InputAdornment>
                  ) : (
                    <></>
                  )}
                </>
              ),
            }}
            PopperProps={{
              className: clsx(cssExports.datePickerPopover, CSSHelper.getClassByDisplayType(displayType)),
              open: pickerOpened,
            }}
            inputFormat={props.inputFormat ?? "yyyy-MM-dd"}
            OpenPickerButtonProps={{ "aria-label": "change date" }}
            onOpen={onPickerOpened}
            onClose={onPickerClosed}
            renderInput={(elements) => (
              <TextField
                {...elements}
                id="date"
                name={props.name}
                label={shrinkLabel ? props.label : overridePlaceholder ?? props.label}
                helperText=""
                type="date"
                InputLabelProps={{
                  shrink: shrinkLabel,
                  classes: {
                    root: clsx(cssExports.inputLabel, cssExports.iconInputLabel),
                    error: cssExports.errorLabel,
                    shrink: cssExports.shrinkedLabel,
                  },
                }}
                FormHelperTextProps={{
                  classes: {
                    root: cssExports.inputHelperText,
                    error: cssExports.errorHelperText,
                  },
                }}
                error={props.error}
                onClick={onClick}
                variant="outlined"
              />
            )}
          />
        </DatePickerLocalization>
      </span>
    </ClickAwayListener>
  );
};

export default SimpleDatePicker;
